import { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { QRCodeDisplay } from '@/components/referral/QRCodeDisplay';
import { Member } from '@/types/member';
import { supabase } from '@/integrations/supabase/client';
import { useContactRequests } from '@/hooks/useContactRequests';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface OtherMemberCardModalProps {
  member: Member;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const OtherMemberCardModal = ({ member, open, onOpenChange }: OtherMemberCardModalProps) => {
  const [referralCode, setReferralCode] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  
  const { sendContactRequest, hasSharedContact, hasPendingRequest } = useContactRequests();
  const { user } = useAuth();

  useEffect(() => {
    const fetchReferralCode = async () => {
      if (!member.id || !open) return;

      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('club_members')
          .select('referral_code')
          .eq('id', member.id)
          .single();

        if (error) throw error;
        setReferralCode(data?.referral_code || '');
      } catch (error) {
        console.error('Error fetching referral code:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReferralCode();
  }, [member.id, open]);

  const referralUrl = referralCode ? `${window.location.origin}/join/${referralCode}` : '';
  
  const isOwnProfile = user?.id === member.user_id;
  const contactAlreadyShared = hasSharedContact(member.user_id || '');
  const requestPending = hasPendingRequest(member.user_id || '');

  const handleRequestContact = async () => {
    if (!user || !member.user_id || isOwnProfile || contactAlreadyShared) return;
    
    setIsSending(true);
    try {
      const success = await sendContactRequest(
        member.user_id,
        `Je souhaite avoir vos coordonnées de contact 📇`
      );
      
      if (success) {
        toast.success(`Demande de contact envoyée à ${member.name} !`);
        onOpenChange(false);
      } else {
        toast.error('Erreur lors de l\'envoi de la demande');
      }
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 overflow-hidden">
        <div className="relative bg-gradient-to-br from-primary/10 via-background to-primary/5 p-8">
          <div className="flex flex-col items-center space-y-6">
            {/* Avatar with Badge */}
            <div className="relative">
              <Avatar className="h-24 w-24 border-4 border-background shadow-lg">
                <AvatarImage src={member.image} alt={member.name} />
                <AvatarFallback className="bg-primary text-primary-foreground text-2xl">
                  {member.name?.charAt(0) || 'M'}
                </AvatarFallback>
              </Avatar>
              <Badge className="absolute -top-2 -right-2 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground">
                🤝 Networking
              </Badge>
            </div>

            {/* Member Info */}
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold text-foreground">{member.name}</h2>
              <p className="text-muted-foreground">
                {member.role} {member.company && `• ${member.company}`}
              </p>
            </div>

            {/* QR Code */}
            <div className="bg-white p-4 rounded-lg shadow-inner">
              {isLoading ? (
                <div className="w-[200px] h-[200px] flex items-center justify-center">
                  <p className="text-muted-foreground">Chargement...</p>
                </div>
              ) : referralCode ? (
                <QRCodeDisplay value={referralUrl} size={200} />
              ) : (
                <div className="w-[200px] h-[200px] flex items-center justify-center">
                  <p className="text-sm text-muted-foreground text-center">
                    Code QR non disponible
                  </p>
                </div>
              )}
            </div>

            {/* Contact Request Button */}
            {!isOwnProfile && contactAlreadyShared && (
              <div className="text-center text-green-600 font-medium py-2">
                ✅ Contact déjà partagé
              </div>
            )}
            
            {!isOwnProfile && requestPending && (
              <div className="text-center text-orange-600 font-medium py-2">
                ⏳ Demande en attente
              </div>
            )}
            
            {!isOwnProfile && !contactAlreadyShared && !requestPending && (
              <Button 
                onClick={handleRequestContact}
                disabled={isSending || isLoading}
                className="w-full"
                size="lg"
              >
                {isSending ? '⏳ Envoi en cours...' : '📇 Demander les contacts'}
              </Button>
            )}

            {/* Instructions */}
            <div className="text-center space-y-2 max-w-md">
              <p className="text-sm text-muted-foreground">
                Scannez ce QR code pour vous connecter avec <span className="font-semibold text-foreground">{member.name}</span>
              </p>
              <p className="text-xs text-muted-foreground">
                Le QR code permet d'envoyer une demande de connexion ou de s'inscrire via le parrainage
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
